// Utilities
import { renderMarkdown } from 'common/utils/helpers';

const extractYoutubeId = (value) => {
  const exp =
    /^(https?:\/\/)?((www\.)?(youtube(-nocookie)?|youtube.googleapis)\.com.*(v\/|v=|vi=|vi\/|e\/|embed\/|user\/.*\/u\/\d+\/)|youtu\.be\/)([_0-9a-z-]{11})/i;
  const match = value.match(exp);
  return match ? match[7] : null;
};

const isValidYoutubeId = (value) => /^[_0-9a-z-]{11}$/i.test(value);

const embedYoutubeVideo = (id) =>
  `<iframe width="560" height="315" src="https://www.youtube.com/embed/${id}" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;

export const displayYoutubePreview = (targetInput) => {
  const $target = $(targetInput);
  const youtubeId = extractYoutubeId($target.val());

  if (youtubeId) {
    $target.val(youtubeId);
  }

  const previewId = $target.val() || $target.attr('placeholder');

  if (isValidYoutubeId(previewId)) {
    $target.addClass('is-success');

    if (!$target.next('.youtube-preview').length) {
      $target.after('<div class="youtube-preview" style="margin-top: 20px;"></div>');
    }

    $target.next('.youtube-preview').html(embedYoutubeVideo(previewId));
  } else {
    $target.removeClass('is-success');
    $target.next('.youtube-preview').html('');
  }
};

export const renderEditor = (element) => {
  const disabled =
    $(element).closest('fieldset').hasClass('is-locked') || $(element).attr('disabled');

  const editor = new SimpleMDE({
    element,
    spellChecker: false,
    toolbar: [
      'heading',
      'bold',
      'italic',
      'link',
      'ordered-list',
      'unordered-list',
      'table',
      ...(disabled ? [] : ['|', 'preview']),
    ],
    status: false,
    forceSync: true,
    previewRender: renderMarkdown,
  });

  editor.codemirror.on('change', () => {
    window.shouldShowExitPageConfirm = true;
  });

  $(element)
    .wrap('<div class="editor-original-textarea"></div>')
    .on('focus', (event) => {
      event.preventDefault();
      editor.codemirror.scrollIntoView();
      editor.codemirror.focus();
    });

  if (disabled) {
    editor.togglePreview();
    $(editor.gui.toolbar).addClass('disabled-for-preview');
  }

  return editor;
};

/**
 * Transform backend coordinate responses into simple array with updatedAt timestamp included
 * @param {Array} responses
 * @returns {Array}
 */
export const flattenCoordinateResponses = (responses) =>
  responses.reduce(
    (dataAccumulator, { answer, updatedAt }) => [
      ...dataAccumulator,
      ...Object.values(answer).reduce(
        (answerAccumulator, { value }) => [
          ...answerAccumulator,
          ...value.map((answerValue) => ({
            ...answerValue,
            updatedAt,
          })),
        ],
        []
      ),
    ],
    []
  );

export const renderNotification = (message, success = true) => {
  document.querySelectorAll('.admin-notification').forEach((el) => el.remove());
  const classList = [
    'admin-notification',
    'hero',
    'is-medium',
    'has-text-centered',
    'has-text-white',
  ];
  const successOrAlert = success ? ['notice', 'is-primary'] : ['alert', 'is-warning'];
  classList.push(...successOrAlert);
  const element = document.createElement('div');
  element.classList.add(...classList);
  element.innerText = message;
  document.body.prepend(element);
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// Checks if number is between start and up to, but not including, end.
export const inRange = (number, start, end, includeEnd = false) => {
  if (includeEnd) return number >= start && number <= end;
  return number >= start && number < end;
};

export const medianFn = (arr) => {
  const mid = Math.floor(arr.length / 2);
  const nums = [...arr].sort((a, b) => a - b);
  return arr.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
};

/* eslint-disable no-param-reassign */
export const modeFn = (numbers) => {
  const hash = numbers.reduce((hsh, current) => {
    if (hsh[current] === undefined) {
      hsh[current] = 1;
    } else {
      hsh[current] += 1;
    }
    return hsh;
  }, {});
  const maxValue = Math.max(...Object.values(hash));
  return Object.keys(hash).find((key) => hash[key] === maxValue);
};
/* eslint-enable no-param-reassign */

// Javascript program to calculate the standered deviation of an array
export const devFn = (arr) => {
  // Creating the mean with Array.reduce
  const mean =
    arr.reduce((acc, curr) => {
      return acc + curr;
    }, 0) / arr.length;

  // Assigning (value - mean) ^ 2 to every array item
  const arr2 = arr.map((k) => {
    return (k - mean) ** 2;
  });

  // Calculating the sum of updated array
  const sum = arr2.reduce((acc, curr) => acc + curr, 0);

  // Calculating the variance
  //  let variance = sum / arr.length

  // Returning the Standered deviation
  return Math.sqrt(sum / arr.length);
};

const singleDigitBins = (data, start, end) => {
  const sanitizedData = [];
  const csvData = [];
  for (let i = Math.floor(start); i <= end; i += 1) {
    const label = i.toString();
    const filtered = data.filter((value) => Number(value) === i);
    const count = filtered.length;
    const percentage = (count / data.length) * 100;
    csvData.push([label, count, percentage]);
    sanitizedData.push({ label, count, percentage });
  }
  return { sanitizedData, csvData };
};

const rangeBins = (data, start, end, decimalDigits) => {
  const rangeDifference = end - start;
  const numberOfBins = 10;
  const rangeIncrement = rangeDifference / numberOfBins;
  const strRangeIncrement = String(rangeIncrement);
  const decimalPlaces = strRangeIncrement.includes('.')
    ? strRangeIncrement.split('.')[1].length
    : 0;
  const sanitizedData = [];
  const csvData = [];
  const smallestDecimalPlace =
    Number(decimalDigits) > decimalPlaces ? Number(decimalDigits) : decimalPlaces;
  const unit = 1 / 10 ** smallestDecimalPlace;

  for (let i = 0; i < numberOfBins; i += 1) {
    const starting = Number(start) + rangeIncrement * i;
    let ending = Number(start) + rangeIncrement * (i + 1);
    const lastBin = i === numberOfBins - 1;
    if (!lastBin) ending -= unit;
    const label = `${starting.toFixed(smallestDecimalPlace)} - ${ending.toFixed(
      smallestDecimalPlace
    )}`;
    const filtered = data.filter((value) => inRange(value, starting, ending, true));
    const count = filtered.length;
    const percentage = parseFloat(((count / data.length) * 100).toFixed(2));
    csvData.push([label, count, percentage]);
    sanitizedData.push({ label, count, percentage });
  }
  return { sanitizedData, csvData };
};

export const createNumericTableAndCsvData = (data, rangeStart, rangeEnd, decimalDigits) => {
  if (!data) return {};
  const rangeDifference = rangeEnd - rangeStart;
  const useSingleDigitBins = rangeDifference <= 10 && decimalDigits === '0';
  const { sanitizedData, csvData } = useSingleDigitBins
    ? singleDigitBins(data, rangeStart, rangeEnd)
    : rangeBins(data, rangeStart, rangeEnd, decimalDigits);
  return { sanitizedData, csvData };
};

/**
 * Switch language
 * @param {string} targetLanguage
 */
export const switchLanguage = (targetLanguage) => {
  const currentPath = window.location.pathname;
  const pathSplit = currentPath.split('/');
  if (pathSplit[1] === 'admin') {
    pathSplit.splice(1, 0, targetLanguage);
  } else {
    pathSplit[1] = targetLanguage;
  }
  const newPath = pathSplit.join('/');
  // eslint-disable-next-line no-alert
  const confirm = window.confirm(
    'Please save your data before switching languages. All unsaved data will be lost. Do you want to proceed ?'
  );
  if (confirm) {
    window.location.pathname = newPath;
  }
};

export function addNotice(message) {
  const noticeRoot = document.querySelector('#react-admin-notice');
  if (!noticeRoot) return;
  const noticeElement = document.createElement('div');
  noticeElement.classList.add(
    'notice',
    'hero',
    'is-medium',
    'is-info',
    'has-text-centered',
    'has-text-white'
  );
  noticeElement.innerHTML = message;
  noticeRoot.appendChild(noticeElement);
}

export function navigateTo(path) {
  window.location.pathname = path;
}
